import {
  enableProdMode,
  provideZonelessChangeDetection,
  importProvidersFrom,
  provideAppInitializer,
  inject,
} from '@angular/core';
import {
  IS_PRODUCTION,
  GOOGLE_OAUTH_CLIENT_ID,
  MICROSOFT_OAUTH_CLIENT_ID,
} from './app/config/config';
import { provideHttpClient } from '@angular/common/http';
import { provideTransloco, translocoConfig } from '@jsverse/transloco';
import { TranslocoHttpLoader } from './app/transloco-http-loader';
import {
  GoogleLoginProvider,
  SocialAuthServiceConfig,
  SocialLoginModule,
  GoogleSigninButtonModule,
} from '@abacritt/angularx-social-login';
import { DeviceDetectorService } from 'ngx-device-detector';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { SpanishPaginatorIntl } from './app/material-spanish-paginator-intl.service';
import { MsalService, MsalModule } from '@azure/msal-angular';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { APP_ROUTES } from './app/app.routes';
import { SharedModule } from './app/shared/shared.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { PublicClientApplication, InteractionType } from '@azure/msal-browser';
import { broadcastResponseToMainFrame } from '@azure/msal-browser/redirect-bridge';
import { MarkdownModule } from 'ngx-markdown';
import { AppComponent } from './app/app.component';
import { prefetchLanguage } from './app/languageInitializer';

if (IS_PRODUCTION) {
  enableProdMode();
  window.console.log = () => {};
}

/**
 * MSAL v5 popups land back on our redirectUri (the app origin) with the auth response in the URL.
 * That page must hand the response to the main window and close, instead of booting the app.
 * Only MSAL responses qualify: their `state` is base64(JSON { id, meta }), optionally followed
 * by `|userState`, so other OAuth callbacks or pasted links still boot the app normally.
 */
function isMsalPopupResponse(): boolean {
  return [window.location.hash, window.location.search].some((part) => {
    const params = new URLSearchParams(part.slice(1));
    const state = params.get('state');
    if (!state || !(params.has('code') || params.has('error'))) return false;
    try {
      const encoded = state.split('|')[0].replace(/-/g, '+').replace(/_/g, '/');
      const libraryState = JSON.parse(atob(encoded));
      return !!libraryState?.id && !!libraryState?.meta;
    } catch {
      return false;
    }
  });
}

const bootstrap = () =>
  bootstrapApplication(AppComponent, {
    providers: [
      importProvidersFrom(
        BrowserModule,
        ReactiveFormsModule,
        FormsModule,
        CommonModule,
        APP_ROUTES,
        SharedModule,
        SocialLoginModule,
        BrowserAnimationsModule,
        NgxChartsModule,
        GoogleSigninButtonModule,
        MsalModule.forRoot(
          new PublicClientApplication({
            auth: {
              clientId: MICROSOFT_OAUTH_CLIENT_ID,
              redirectUri: window.location.origin,
              authority: 'https://login.microsoftonline.com/common',
            },
            cache: {
              cacheLocation: 'localStorage',
            },
          }),
          {
            interactionType: InteractionType.Popup,
            authRequest: {
              scopes: ['user.read'],
            },
          },
          {
            interactionType: InteractionType.Popup,
            protectedResourceMap: new Map(),
          },
        ),
        MarkdownModule.forRoot(),
      ),
      provideZonelessChangeDetection(),
      provideHttpClient(),
      provideTransloco({
        config: translocoConfig({
          availableLangs: ['en', 'es'],
          defaultLang: 'es',
          fallbackLang: 'es',
          prodMode: IS_PRODUCTION,
        }),
        loader: TranslocoHttpLoader,
      }),
      provideAppInitializer(prefetchLanguage),
      // MSAL failing must only break Microsoft login, never the whole app
      provideAppInitializer(() =>
        inject(MsalService)
          .instance.initialize()
          .catch((err) => console.error('MSAL initialization failed', err)),
      ),
      {
        provide: 'SocialAuthServiceConfig',
        useValue: {
          autoLogin: false,
          providers: [
            {
              id: GoogleLoginProvider.PROVIDER_ID,
              provider: new GoogleLoginProvider(GOOGLE_OAUTH_CLIENT_ID),
            },
          ],
        } as SocialAuthServiceConfig,
      },
      {
        provide: DeviceDetectorService,
      },
      { provide: MatPaginatorIntl, useClass: SpanishPaginatorIntl },
      MsalService,
    ],
  }).catch((err) => console.error(err));

if (isMsalPopupResponse()) {
  broadcastResponseToMainFrame().catch((err) => console.error(err));
} else {
  bootstrap();
}
